import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Bed, Wrench, Users } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

export default function Hostel() {
  const { user } = useAuthStore();
  const isManager = ['admin', 'registrar', 'superadmin'].includes(user?.role || '');
  const [tab, setTab] = useState<'rooms' | 'allocations' | 'maintenance'>(isManager ? 'rooms' : 'allocations');

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Hostel & Accommodation</h1><p className="page-subtitle">{isManager ? 'Rooms, allocations, and maintenance' : 'Your room and maintenance requests'}</p></div>
      </div>

      <div className="tabs">
        {isManager && <button className={`tab ${tab === 'rooms' ? 'active' : ''}`} onClick={() => setTab('rooms')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Bed size={14} />Hostels & Rooms</span></button>}
        <button className={`tab ${tab === 'allocations' ? 'active' : ''}`} onClick={() => setTab('allocations')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Users size={14} />{isManager ? 'Allocations' : 'My Room'}</span></button>
        <button className={`tab ${tab === 'maintenance' ? 'active' : ''}`} onClick={() => setTab('maintenance')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wrench size={14} />Maintenance</span></button>
      </div>

      {tab === 'rooms' && isManager && <RoomsPanel />}
      {tab === 'allocations' && <AllocationsPanel isManager={isManager} />}
      {tab === 'maintenance' && <MaintenancePanel isManager={isManager} />}
    </div>
  );
}

function RoomsPanel() {
  const [hostels, setHostels] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [selectedHostel, setSelectedHostel] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showHostelModal, setShowHostelModal] = useState(false);
  const [showRoomModal, setShowRoomModal] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const h = await api.get('/hostel/hostels');
      setHostels(h.data.data || []);
      const r = await api.get('/hostel/rooms', { params: selectedHostel ? { hostel_id: selectedHostel } : {} });
      setRooms(r.data.data || []);
    } finally { setLoading(false); }
  }, [selectedHostel]);
  useEffect(() => { doFetch(); }, [doFetch]);

  return (
    <>
      <div className="grid-3" style={{ marginBottom: 20 }}>
        {hostels.map((h: any) => (
          <div key={h.id} className="card" style={{ cursor: 'pointer', border: selectedHostel === h.id ? '2px solid var(--primary)' : undefined }} onClick={() => setSelectedHostel(h.id === selectedHostel ? '' : h.id)}>
            <div style={{ fontWeight: 700 }}>{h.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{h.gender} · {h.room_count} rooms</div>
            <div style={{ marginTop: 8, fontSize: 13 }}>{h.occupied_count}/{h.total_capacity} occupied</div>
            <div style={{ background: 'var(--border)', borderRadius: 8, height: 8, marginTop: 6, overflow: 'hidden' }}>
              <div style={{ width: `${h.total_capacity > 0 ? (h.occupied_count / h.total_capacity) * 100 : 0}%`, height: '100%', background: 'var(--primary)' }} />
            </div>
          </div>
        ))}
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px dashed var(--border)' }} onClick={() => setShowHostelModal(true)}>
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}><Plus size={20} /><div style={{ fontSize: 13, marginTop: 4 }}>Add Hostel</div></div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowRoomModal(true)} disabled={hostels.length === 0}><Plus size={14} />Add Room</button>
      </div>
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr><th>Room</th><th>Hostel</th><th>Capacity</th><th>Occupied</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : rooms.length === 0 ? <tr><td colSpan={4}><div className="empty-state"><Bed size={40} /><p>No rooms yet</p></div></td></tr>
              : rooms.map((r: any) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500 }}>{r.room_number}</td>
                  <td>{r.hostel_name}</td>
                  <td>{r.capacity}</td>
                  <td>{r.occupied}/{r.capacity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showHostelModal && <AddHostelModal onClose={() => setShowHostelModal(false)} onSaved={() => { setShowHostelModal(false); doFetch(); }} />}
      {showRoomModal && <AddRoomModal hostels={hostels} onClose={() => setShowRoomModal(false)} onSaved={() => { setShowRoomModal(false); doFetch(); }} />}
    </>
  );
}

function AddHostelModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ gender: 'mixed' });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try { await api.post('/hostel/hostels', form); toast.success('Hostel created'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Add Hostel</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Name *</label><input className="form-input" required value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Gender</label><select className="form-input form-select" value={form.gender} onChange={e => set('gender', e.target.value)}><option value="mixed">Mixed</option><option value="male">Male</option><option value="female">Female</option></select></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Add'}</button></div>
        </form>
      </div>
    </div>
  );
}

function AddRoomModal({ hostels, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ capacity: 1 });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.hostel_id || !form.room_number) { toast.error('Hostel and room number are required'); return; }
    setLoading(true);
    try { await api.post('/hostel/rooms', form); toast.success('Room added'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Add Room</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Hostel *</label><select className="form-input form-select" required value={form.hostel_id || ''} onChange={e => set('hostel_id', e.target.value)}><option value="">Select...</option>{hostels.map((h: any) => <option key={h.id} value={h.id}>{h.name}</option>)}</select></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Room Number *</label><input className="form-input" required value={form.room_number || ''} onChange={e => set('room_number', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Capacity</label><input type="number" min={1} className="form-input" value={form.capacity} onChange={e => set('capacity', e.target.value)} /></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Add'}</button></div>
        </form>
      </div>
    </div>
  );
}

function AllocationsPanel({ isManager }: { isManager: boolean }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/hostel/allocations'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  const checkout = async (id: string) => {
    if (!window.confirm('Check out this student from their room?')) return;
    try { await api.patch(`/hostel/allocations/${id}/checkout`); toast.success('Checked out'); doFetch(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  return (
    <>
      {isManager && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Allocate Room</button></div>}
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr>{isManager && <th>Student</th>}<th>Hostel</th><th>Room</th><th>Academic Year</th><th>Status</th>{isManager && <th></th>}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : items.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><Bed size={40} /><p>No room allocations yet</p></div></td></tr>
              : items.map((a: any) => (
                <tr key={a.id}>
                  {isManager && <td>{a.student_first_name} {a.student_last_name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.student_number}</div></td>}
                  <td>{a.hostel_name}</td>
                  <td style={{ fontWeight: 500 }}>{a.room_number}</td>
                  <td>{a.academic_year || '—'}</td>
                  <td><span className={`badge badge-${a.status === 'active' ? 'success' : 'gray'}`} style={{ textTransform: 'capitalize' }}>{a.status.replace('_', ' ')}</span></td>
                  {isManager && <td>{a.status === 'active' && <button className="btn btn-secondary btn-sm" onClick={() => checkout(a.id)}>Check Out</button>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <AllocateModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
    </>
  );
}

function AllocateModal({ onClose, onSaved }: any) {
  const [rooms, setRooms] = useState<any[]>([]);
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => { api.get('/hostel/rooms').then(r => setRooms((r.data.data || []).filter((x: any) => x.occupied < x.capacity))); }, []);
  useEffect(() => {
    if (searchStu.length >= 2) api.get('/students', { params: { search: searchStu, limit: 10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.room_id || !form.student_id) { toast.error('Room and student are required'); return; }
    setLoading(true);
    try { await api.post('/hostel/allocations', form); toast.success('Room allocated'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Allocate Room</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Student *</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e => setSearchStu(e.target.value)} />
              {stuResults.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id', s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Room *</label><select className="form-input form-select" required value={form.room_id || ''} onChange={e => set('room_id', e.target.value)}><option value="">Select room with space...</option>{rooms.map((r: any) => <option key={r.id} value={r.id}>{r.hostel_name} — {r.room_number} ({r.occupied}/{r.capacity})</option>)}</select></div>
            <div className="form-group"><label className="form-label">Academic Year</label><input className="form-input" placeholder="2024/2025" value={form.academic_year || ''} onChange={e => set('academic_year', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Allocate'}</button></div>
        </form>
      </div>
    </div>
  );
}

function MaintenancePanel({ isManager }: { isManager: boolean }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/hostel/maintenance'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  const resolve = async (id: string) => {
    try { await api.patch(`/hostel/maintenance/${id}/resolve`); toast.success('Marked resolved'); doFetch(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Report Issue</button></div>
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr><th>Room</th><th>Issue</th><th>Reported By</th><th>Status</th>{isManager && <th></th>}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : items.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><Wrench size={40} /><p>No maintenance requests</p></div></td></tr>
              : items.map((m: any) => (
                <tr key={m.id}>
                  <td>{m.room_number ? `${m.hostel_name} — ${m.room_number}` : '—'}</td>
                  <td style={{ fontSize: 13 }}>{m.issue}</td>
                  <td>{m.reported_by_first_name} {m.reported_by_last_name}</td>
                  <td><span className={`badge badge-${m.status === 'resolved' ? 'success' : 'warning'}`} style={{ textTransform: 'capitalize' }}>{m.status}</span></td>
                  {isManager && <td>{m.status === 'open' && <button className="btn btn-secondary btn-sm" onClick={() => resolve(m.id)}>Resolve</button>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <ReportIssueModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
    </>
  );
}

function ReportIssueModal({ onClose, onSaved }: any) {
  const [rooms, setRooms] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  useEffect(() => { api.get('/hostel/rooms').then(r => setRooms(r.data.data || [])); }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.issue) { toast.error('Describe the issue'); return; }
    setLoading(true);
    try { await api.post('/hostel/maintenance', form); toast.success('Reported'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Report Maintenance Issue</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Room (optional)</label><select className="form-input form-select" value={form.room_id || ''} onChange={e => set('room_id', e.target.value)}><option value="">General / not room-specific</option>{rooms.map((r: any) => <option key={r.id} value={r.id}>{r.hostel_name} — {r.room_number}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Issue *</label><textarea className="form-input" rows={3} required value={form.issue || ''} onChange={e => set('issue', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit'}</button></div>
        </form>
      </div>
    </div>
  );
}
